import { useState } from "react";
import { useApi } from "@/api/useApi";
import type { ExpensePolicy } from "@/api/types";
import { relativeTime } from "@/format";

type Result = { ok: boolean; text: string } | undefined;

interface Sent {
  managers: number;
  managerReports: number;
  payrollReports: number;
  executives: number;
  executiveReports: number;
}

function reports(count: number): string {
  return `${String(count)} ${count === 1 ? "report" : "reports"}`;
}

/**
 * How often managers and payroll are reminded of reports waiting on them, and a button to remind
 * them now. The days are saved with the rest of Settings.
 */
export function RemindersCard({
  policy,
  everyDays,
  onEveryDays,
  onSent,
}: {
  policy: ExpensePolicy;
  everyDays: string;
  onEveryDays: (value: string) => void;
  onSent: () => void;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result>();

  function send() {
    setBusy(true);
    setResult(undefined);
    api
      .post<Sent>("/api/expenses/settings/reminders/send")
      .then((sent) => {
        const managers =
          sent.managers === 0
            ? "No manager has a report waiting."
            : `Reminded ${String(sent.managers)} ${sent.managers === 1 ? "manager" : "managers"} of ${reports(sent.managerReports)}.`;
        const payroll =
          sent.payrollReports === 0
            ? "Nothing is waiting for payroll."
            : `Reminded payroll of ${reports(sent.payrollReports)}.`;
        const executives =
          sent.executives === 0
            ? "No executive has a report waiting."
            : `Reminded ${String(sent.executives)} ${sent.executives === 1 ? "executive" : "executives"} of ${reports(sent.executiveReports)}.`;
        setResult({ ok: true, text: `${managers} ${payroll} ${executives}` });
        onSent();
      })
      .catch((caught: unknown) =>
        setResult({ ok: false, text: caught instanceof Error ? caught.message : String(caught) }),
      )
      .finally(() => setBusy(false));
  }

  return (
    <section className="card stack">
      <h2>Reminders</h2>
      <label>
        Remind every
        <span className="input-suffix">
          <input
            value={everyDays}
            onChange={(e) => onEveryDays(e.target.value)}
            inputMode="numeric"
            pattern="[0-9]+"
          />
          <span aria-hidden="true">days</span>
        </span>
      </label>
      <p className="muted">
        At 8am Pacific, each manager gets one email listing the reports waiting for their approval,
        the payroll mailbox one listing the approved reports waiting to be paid, and each executive
        one listing the reports payroll asked them to approve. Reports that arrived in the last day
        wait for the next one. Set to 0 to turn reminders off.
      </p>
      <dl className="facts">
        <div>
          <dt>Last sent</dt>
          <dd>{policy.remindersSentAt ? relativeTime(policy.remindersSentAt) : "Never"}</dd>
        </div>
      </dl>
      {result && (
        <div className={result.ok ? "success" : "error"} role="status">
          {result.text}
        </div>
      )}
      <button type="button" className="btn secondary" disabled={busy} onClick={send}>
        {busy ? "Sending…" : "Send reminders now"}
      </button>
    </section>
  );
}
